import { ProjectNode, ProjectData, NodeOperation, ChecklistItem } from '../types/projectData';
import { initializeViewState, getGanttOrder, setGanttOrder } from './viewOrdering';
import { log } from '@/shared/utils/logger';
import { DEFAULT_KANBAN_COLUMNS } from '@/shared/utils/kanbanState';
import { normalizeTaskStateUpdate } from '@/shared/utils/taskStateSync';
import {
  buildCanonicalNodeTypeConversionUpdates,
  buildSafeDocumentNormalizationUpdates,
  repairInvalidContainerNodeTypes,
} from '@/shared/utils/nodeTypeConversionPolicy';
import { applyParentCreateDefaults, resolveParentCreateDefaults } from '@/shared/utils/createNodeDefaults';

// Core utilities
export const cloneProjectData = (data: ProjectData): ProjectData => {
  const cloned = structuredClone(data);
  const nodes = Array.isArray(cloned?.nodes) ? cloned.nodes : [];
  const rootNodeIds = Array.isArray(data?.rootNodeIds)
    ? [...data.rootNodeIds]
    : buildCanonicalRootNodeIds(nodes);

  if (!Array.isArray(data?.rootNodeIds)) {
    log.debug('cloneProjectData', 'Rebuilding canonical root order from nodes for project data without rootNodeIds');
  }

  return {
    ...cloned,
    nodes,
    rootNodeIds,
  };
};

// Helper function for internal use
const flattenTree = (nodes: ProjectNode[], parentId?: string): ProjectNode[] => {
  const result: ProjectNode[] = [];
  nodes.forEach((node, i) => {
    result.push({ ...node, parentId: parentId ?? node.parentId, order: i, children: undefined });
    if (node.children) result.push(...flattenTree(node.children, node.id));
  });
  return result;
};

export const compareNodesForCanonicalOrder = (left: Pick<ProjectNode, 'order' | 'title' | 'id'>, right: Pick<ProjectNode, 'order' | 'title' | 'id'>): number => {
  const leftOrder = typeof left.order === 'number' ? left.order : 0;
  const rightOrder = typeof right.order === 'number' ? right.order : 0;
  if (leftOrder !== rightOrder) return leftOrder - rightOrder;
  const titleComparison = (left.title || '').localeCompare(right.title || '');
  if (titleComparison !== 0) return titleComparison;
  return (left.id || '').localeCompare(right.id || '');
};

export const buildCanonicalRootNodeIds = (nodes: Array<Pick<ProjectNode, 'id' | 'parentId' | 'order' | 'title'>>): string[] =>
  nodes
    .filter(node => !node.parentId)
    .sort(compareNodesForCanonicalOrder)
    .map(node => node.id);

const recomputeCanonicalRootNodeIds = (data: ProjectData): void => {
  data.rootNodeIds = buildCanonicalRootNodeIds(data.nodes);
};

const touchProjectData = (data: ProjectData): void => {
  data.updatedAt = new Date().toISOString();
};

const ensureNodeViewState = (node: ProjectNode): ProjectNode => {
  if (!node.viewState || Object.keys(node.viewState).length === 0) {
    initializeViewState(node);
  }
  return node;
};

const stripEmptyIncomingViewState = <T extends Partial<ProjectNode>>(incoming: T): T => {
  if (
    incoming.viewState === undefined ||
    incoming.viewState === null ||
    (typeof incoming.viewState === 'object' && Object.keys(incoming.viewState).length === 0)
  ) {
    const { viewState: _ignoredViewState, ...rest } = incoming as T & { viewState?: ProjectNode['viewState'] };
    return rest as T;
  }
  return incoming;
};

const normalizeIncomingScheduleFields = <T extends Partial<ProjectNode>>(
  incoming: T,
  nodeType?: ProjectNode['type']
): T => {
  const scheduleFields = nodeType === 'milestone'
    ? ['date', 'startDate', 'endDate', 'dueDate']
    : ['startDate', 'endDate', 'dueDate'];

  let nextIncoming: T = incoming;

  scheduleFields.forEach((field) => {
    const record = nextIncoming as Record<string, unknown>;
    if (Object.prototype.hasOwnProperty.call(nextIncoming, field) && record[field] === undefined) {
      const clone = { ...record };
      clone[field] = null;
      nextIncoming = clone as T;
    }
  });

  return nextIncoming;
};

const isNamespacedNodeId = (id: unknown): id is string =>
  typeof id === 'string' && id.includes(':') && id.split(':').length === 2;

const rawNodeId = (id: unknown): string =>
  isNamespacedNodeId(id) ? id.split(':')[1] : String(id ?? '');

const projectIdFromNodeId = (id: unknown, fallback?: string): string | undefined =>
  isNamespacedNodeId(id) ? id.split(':')[0] : fallback;

const findNodeIndexByFlexibleId = (nodes: ProjectNode[], nodeId: string): number => {
  const exactIndex = nodes.findIndex((node) => node.id === nodeId);
  if (exactIndex >= 0) {
    return exactIndex;
  }

  const normalizedNodeId = rawNodeId(nodeId);
  return nodes.findIndex((node) => rawNodeId(node.id) === normalizedNodeId);
};

const normalizeDependencyIdsForNode = (
  nodes: ProjectNode[],
  ownerNode: ProjectNode,
  dependencies: string[],
): string[] => {
  const idsAreNamespaced = nodes.some((node) => isNamespacedNodeId(node.id));
  const ownerProjectId = projectIdFromNodeId(ownerNode.id, ownerNode.projectId);

  return dependencies
    .filter((dependencyId): dependencyId is string => typeof dependencyId === 'string' && dependencyId.length > 0)
    .map((dependencyId) => {
      const dependencyRawId = rawNodeId(dependencyId);

      if (!idsAreNamespaced) {
        return dependencyRawId;
      }

      if (isNamespacedNodeId(dependencyId)) {
        return dependencyId;
      }

      const matchingNode = nodes.find((node) => rawNodeId(node.id) === dependencyRawId);
      if (matchingNode?.id && isNamespacedNodeId(matchingNode.id)) {
        return matchingNode.id;
      }

      return ownerProjectId ? `${ownerProjectId}:${dependencyRawId}` : dependencyRawId;
    });
};

// Tree operations
export function getAllDescendantIds(data: ProjectData, nodeId: string): string[] {
  const descendants: string[] = [];
  const visited = new Set<string>([nodeId]);
  const pending = data.nodes.filter(node => node.parentId === nodeId).reverse();
  while (pending.length > 0) {
    const node = pending.pop()!;
    if (visited.has(node.id)) continue;
    visited.add(node.id);
    descendants.push(node.id);
    pending.push(...data.nodes.filter(child => child.parentId === node.id).reverse());
  }
  return descendants;
}

export function buildTree<T extends ProjectNode>(data: Omit<ProjectData, 'nodes'> & { nodes: T[] }): T[] {
  type TreeNode = T & { children: TreeNode[] };
  const nodeMap = new Map(data.nodes.map(n => [n.id, { ...n, children: [] } as TreeNode]));
  const roots: TreeNode[] = [];

  data.nodes.forEach(node => {
    const n = nodeMap.get(node.id)!;
    if (node.parentId && nodeMap.has(node.parentId)) {
      nodeMap.get(node.parentId)!.children.push(n);
    } else {
      roots.push(n);
    }
  });

  const sort = (nodes: TreeNode[]) => {
    // Use Gantt-specific order from viewState
    nodes.sort((a, b) => getGanttOrder(a) - getGanttOrder(b));
    nodes.forEach(n => n.children && sort(n.children));
  };
  sort(roots);
  return roots as T[];
}

// CRUD operations
export function createNode(data: ProjectData, newNode: Partial<ProjectNode>, parentId?: string, index?: number): ProjectData {
  const cloned = cloneProjectData(data);
  const idsAreNamespaced = cloned.nodes.some(n => typeof n.id === 'string' && n.id.includes(':'));
  const requestedParentId = parentId ?? newNode.parentId;
  const inferredProjectId = (() => {
    if (typeof requestedParentId === 'string' && requestedParentId.includes(':')) {
      return requestedParentId.split(':', 1)[0];
    }
    if (typeof newNode.projectId === 'string' && newNode.projectId && newNode.projectId !== 'default') {
      return newNode.projectId;
    }
    if (typeof data.projectId === 'string' && data.projectId && data.projectId !== 'default') {
      return data.projectId;
    }
    return undefined;
  })();
  const namespaceId = (value: string | undefined): string | undefined => {
    if (!value) return value;
    if (!idsAreNamespaced || !inferredProjectId || value.includes(':')) return value;
    return `${inferredProjectId}:${value}`;
  };
  const normalizedParentId = namespaceId(requestedParentId);

  const normalizedIncomingNode: Partial<ProjectNode> = applyParentCreateDefaults({
    ...newNode,
    ...(typeof newNode.id === 'string' ? { id: namespaceId(newNode.id) } : {}),
    ...(typeof newNode.parentId === 'string' ? { parentId: namespaceId(newNode.parentId) } : {}),
    ...(Array.isArray(newNode.dependencies)
      ? { dependencies: newNode.dependencies.map((dependencyId) => namespaceId(dependencyId) || dependencyId) }
      : {}),
  }, resolveParentCreateDefaults(cloned.nodes, normalizedParentId ?? newNode.parentId));

  // Deduplication guard: if a node with the same ID already exists, merge instead of creating a duplicate
  if (normalizedIncomingNode.id) {
    const existingIndex = cloned.nodes.findIndex(n => n.id === normalizedIncomingNode.id);
    if (existingIndex !== -1) {
      const existingNode = cloned.nodes[existingIndex];
      const normalizedNewNode = normalizeIncomingScheduleFields(
        stripEmptyIncomingViewState(normalizedIncomingNode as ProjectNode),
        ((normalizedIncomingNode as ProjectNode).type || existingNode.type) as ProjectNode['type']
      );
      const resolvedParentId =
        normalizedParentId !== undefined
          ? normalizedParentId
          : ((normalizedIncomingNode as ProjectNode).parentId ?? existingNode.parentId);
      const siblingCount = cloned.nodes.filter(n => (n.parentId || undefined) === resolvedParentId && n.id !== normalizedIncomingNode.id).length;
      const finalIndex =
        index ??
        (typeof (normalizedIncomingNode as ProjectNode).order === 'number'
          ? (normalizedIncomingNode as ProjectNode).order!
          : (typeof existingNode.order === 'number' ? existingNode.order : siblingCount));

      let authoritativeNode: ProjectNode = {
        ...existingNode,
        ...normalizedNewNode,
        parentId: resolvedParentId,
        order: finalIndex,
        updatedAt: new Date().toISOString()
      } as ProjectNode;

      authoritativeNode = ensureNodeViewState(authoritativeNode);
      if (authoritativeNode.type === 'task') {
        authoritativeNode = { ...authoritativeNode, ...normalizeTaskStateUpdate(authoritativeNode as Partial<ProjectNode>, authoritativeNode as Partial<ProjectNode>) } as ProjectNode;
      }

      cloned.nodes[existingIndex] = authoritativeNode;
      recomputeCanonicalRootNodeIds(cloned);
      touchProjectData(cloned);
      return cloned;
    }
  }
  const siblings = cloned.nodes.filter(n => (n.parentId ?? undefined) === (normalizedParentId ?? undefined));
  const finalIndex = index ?? siblings.length;
  const sourceNode = normalizedIncomingNode as ProjectNode;
  
  // Check if this is a restoration operation (node has id and timestamps)
  const isRestoration = !!(sourceNode.id && sourceNode.createdAt);
  
  let node: ProjectNode;
  
  if (isRestoration) {
    // For restoration, preserve ALL original data
    node = {
      ...sourceNode,
      // Only update parentId if explicitly provided as parameter (not undefined)
      ...(normalizedParentId !== undefined ? { parentId: normalizedParentId } : {}),
      // Preserve original order unless index is explicitly provided
      order: index !== undefined ? finalIndex : (sourceNode.order ?? finalIndex)
    };
  } else {
    const generatedNodeId = `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    // For new nodes, apply defaults then override with provided data
    const nodeDefaults: ProjectNode = {
      id: sourceNode.id || namespaceId(generatedNodeId) || generatedNodeId,
      type: 'task',
      // i18n-raw-ui-literal-exempt: Persisted default task title; never localize stored user data.
      title: 'New Item',
      completionState: 'not-started',
      priority: 'medium',
      progress: 0,
      assignees: [],
      tags: [],
      dependencies: [],
      linkedDocs: [],
      attachments: [],
      attachmentCount: 0,
      hasImages: false,
      hasDocuments: false,
      viewState: {},
      version: 1,
      createdAt: '',
      updatedAt: '',
      customFields: {},
      order: finalIndex,
    };
    node = {
      ...nodeDefaults,
      ...sourceNode,
      id: sourceNode.id || nodeDefaults.id,
      type: sourceNode.type ?? nodeDefaults.type,
      title: sourceNode.title ?? nodeDefaults.title,
      completionState: sourceNode.completionState ?? nodeDefaults.completionState,
      priority: sourceNode.priority ?? nodeDefaults.priority,
      progress: sourceNode.progress ?? nodeDefaults.progress,
      assignees: sourceNode.assignees ?? [],
      tags: sourceNode.tags ?? [],
      dependencies: sourceNode.dependencies ?? [],
      linkedDocs: sourceNode.linkedDocs ?? [],
      attachments: sourceNode.attachments ?? [],
      attachmentCount: sourceNode.attachmentCount ?? 0,
      hasImages: sourceNode.hasImages ?? false,
      hasDocuments: sourceNode.hasDocuments ?? false,
      viewState: sourceNode.viewState ?? {},
      version: sourceNode.version ?? 1,
      customFields: sourceNode.customFields ?? {},
      parentId: normalizedParentId,
      order: finalIndex,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    // Initialize viewState with proper ordering
    initializeViewState(node);
    
    // Preserve an explicit projectId chosen by the caller.
    // In aggregated/multi-project views, projectData.projectId is only the active shell context
    // and must not overwrite the task's actual target project.
    if (!node.projectId && inferredProjectId) {
      node.projectId = inferredProjectId;
    } else if (!node.projectId && data.projectId) {
      node.projectId = data.projectId;
    }
  }

  if (node.type === 'task') {
    node = { ...node, ...normalizeTaskStateUpdate(node as Partial<ProjectNode>, node as Partial<ProjectNode>) } as ProjectNode;
    if (!Array.isArray(node.checklist)) node.checklist = [];
  } else if (node.type === 'group') {
    if (!Array.isArray(node.checklist) && Array.isArray((node as ProjectNode & { checklists?: ChecklistItem[] }).checklists)) {
      node.checklist = [...((node as ProjectNode & { checklists?: ChecklistItem[] }).checklists || [])];
    }
    if (Array.isArray(node.checklist)) {
      (node as ProjectNode & { checklists?: ChecklistItem[] }).checklists = [...node.checklist];
    }
  } else {
    delete node.checklist;
    delete (node as ProjectNode & { checklists?: ChecklistItem[] }).checklists;
  }
  
  siblings.filter(n => (n.order || 0) >= finalIndex).forEach(n => n.order = (n.order || 0) + 1);
  cloned.nodes.push(node);
  recomputeCanonicalRootNodeIds(cloned);
  touchProjectData(cloned);
  return cloned;
}

export function updateNode(data: ProjectData, nodeId: string, updates: Partial<ProjectNode>): ProjectData {
  const cloned = cloneProjectData(data);
  const resolvedNodeIndex = findNodeIndexByFlexibleId(cloned.nodes, nodeId);
  const node = resolvedNodeIndex >= 0 ? cloned.nodes[resolvedNodeIndex] : undefined;
  
  // DEBUG: Log whether node was found
  log.debug('updateNode', 'Looking for nodeId:', nodeId);
  log.debug('updateNode', 'Total nodes in data:', cloned.nodes.length);
  log.debug('updateNode', 'Node found:', !!node);
  if (!node && cloned.nodes.length > 0) {
    log.debug('updateNode', 'Available node IDs:', cloned.nodes.map(n => n.id).slice(0, 10));
  }
  
  if (node) {
    // Handle checklist delta updates from WebSocket broadcasts
    // Backend emits three shapes:
    // 1) Add:   { checklist: [ { id, text, checked, ... } ] } -> append/merge by id
    // 2) Update:{ checklistUpdate: { itemId, text?, checked? } } -> mutate item fields
    // 3) Delete:{ checklistDelete: { itemId } } -> remove item by id
    // IMPORTANT: Work on a safe copy so we never mutate the caller's object.
    // Mutating the original updates would strip fields (like checklist) from the
    // WebSocket payload before it reaches the backend.
    type ChecklistDelta = {
      added?: Partial<ChecklistItem>[];
      updated?: { itemId: string } & Partial<ChecklistItem>;
      deleted?: { itemId: string };
    };
    type UpdateRecord = Partial<ProjectNode> & {
      checklists?: ChecklistItem[];
      checklistUpdate?: { itemId?: string; text?: string; checked?: boolean };
      checklistDelete?: { itemId?: string };
      checklistDelta?: ChecklistDelta;
    };
    let u: UpdateRecord = { ...updates };
    if (typeof u.type === 'string' && u.type !== node.type) {
      const conversion = u.type === 'document'
        ? buildSafeDocumentNormalizationUpdates(
            node,
            u.type as ProjectNode['type'],
            cloned.nodes,
            u as Partial<ProjectNode>,
          )
        : buildCanonicalNodeTypeConversionUpdates(
            node,
            u.type as ProjectNode['type'],
            cloned.nodes,
            u as Partial<ProjectNode>,
          );
      if (!conversion.allowed || !conversion.updates) {
        log.warn('updateNode', `Rejected invalid node type conversion for ${nodeId}: ${conversion.reason}`);
        return cloned;
      }
      u = conversion.updates as UpdateRecord;
    }
    const shouldNormalizeTaskState =
      (node.type === 'task' && updates.type !== 'group' && updates.type !== 'milestone' && updates.type !== 'document') ||
      updates.type === 'task';
    if (shouldNormalizeTaskState) {
      u = normalizeTaskStateUpdate(u as Partial<ProjectNode>, node as Partial<ProjectNode>) as UpdateRecord;
    }
    const targetSupportsChecklist = (u.type || node.type) === 'task' || (u.type || node.type) === 'group';
    if (!targetSupportsChecklist) {
      delete u.checklist;
      delete u.checklists;
      delete u.checklistDelta;
      delete u.checklistUpdate;
      delete u.checklistDelete;
    } else {
      // Ensure checklist array exists before mutations
      if (!Array.isArray(node.checklist) && Array.isArray((node as ProjectNode & { checklists?: ChecklistItem[] }).checklists)) {
        node.checklist = [...((node as ProjectNode & { checklists?: ChecklistItem[] }).checklists || [])];
      }
      if (!Array.isArray(node.checklist)) {
        node.checklist = [];
      }
      if (!Array.isArray(u.checklist) && Array.isArray(u.checklists)) {
        u.checklist = u.checklists;
      }
      // Add/merge or replace checklist items.
      // Authoritative saved task/group snapshots arrive with a full checklist
      // array and must replace the existing list so removed items do not linger.
      const shouldReplaceChecklist =
        Array.isArray(u?.checklist) &&
        (
          typeof u?.id === 'string' ||
          Object.prototype.hasOwnProperty.call(u, 'updatedAt') ||
          Object.prototype.hasOwnProperty.call(u, 'checklistSummary')
        );

      // Add/merge checklist items (supports unified array or legacy delta)
      if (Array.isArray(u?.checklist)) {
        type ChecklistUpdateItem = Partial<ChecklistItem> & { id?: string };
        const incomingItems = u.checklist as ChecklistUpdateItem[];
        if (shouldReplaceChecklist) {
          node.checklist = incomingItems.map((item) => ({ ...item })) as ChecklistItem[];
        } else if (incomingItems.length > 0) {
          const list: ChecklistUpdateItem[] = Array.isArray(node.checklist) ? [...node.checklist] : [];
          incomingItems.forEach((item) => {
            if (!item || !item.id) return;
            const idx = list.findIndex((i) => i && i.id === item.id);
            if (idx >= 0) {
              list[idx] = { ...list[idx], ...item };
            } else {
              list.push({ ...item });
            }
          });
          node.checklist = list as ChecklistItem[];
        }
        // Prevent Object.assign from overwriting checklist with partial array
        delete u.checklist;
        delete u.checklists;
      }
      // Legacy delta compatibility: checklistDelta { added?:[], updated?:{}, deleted?:{} }
      if (u?.checklistDelta && typeof u.checklistDelta === 'object') {
        const list: Partial<ChecklistItem>[] = node.checklist || [];
        const delta = u.checklistDelta;
        if (Array.isArray(delta.added)) {
          delta.added.forEach((item) => {
            if (!item || !item.id) return;
            const idx = list.findIndex((i) => i && i.id === item.id);
            if (idx >= 0) list[idx] = { ...list[idx], ...item }; else list.push({ ...item });
          });
        }
        if (delta.updated && delta.updated.itemId) {
          const { itemId, ...fields } = delta.updated;
          const idx = list.findIndex((i) => i && i.id === itemId);
          if (idx >= 0) list[idx] = { ...list[idx], ...fields };
        }
        if (delta.deleted && delta.deleted.itemId) {
          const { itemId } = delta.deleted;
          node.checklist = list.filter((i) => i && i.id !== itemId) as ChecklistItem[];
        } else {
          node.checklist = list as ChecklistItem[];
        }
        delete u.checklistDelta;
      }
      // Update a specific checklist item (legacy)
      if (u?.checklistUpdate && typeof u.checklistUpdate === 'object') {
        const { itemId, ...fields } = u.checklistUpdate;
        if (itemId) {
          const list: Partial<ChecklistItem>[] = node.checklist || [];
          const idx = list.findIndex((i) => i && i.id === itemId);
          if (idx >= 0) {
            list[idx] = { ...list[idx], ...fields };
            node.checklist = list as ChecklistItem[];
          }
        }
        delete u.checklistUpdate;
      }
      // Delete a checklist item (legacy)
      if (u?.checklistDelete && typeof u.checklistDelete === 'object') {
        const { itemId } = u.checklistDelete;
        if (itemId) {
          const list: Partial<ChecklistItem>[] = node.checklist || [];
          node.checklist = list.filter((i) => i && i.id !== itemId) as ChecklistItem[];
        }
        delete u.checklistDelete;
      }
    }
    // Groups cannot have dependencies - enforce this rule
    if (u.type === 'group') {
      u.dependencies = [];
    }

    // CRITICAL FIX: Deep clone array fields to ensure React detects changes
    // When updating dependencies array, create a NEW array reference
    if (u.dependencies && Array.isArray(u.dependencies)) {
      u.dependencies = normalizeDependencyIdsForNode(cloned.nodes, node, u.dependencies);
    }
    if (u.assignees && Array.isArray(u.assignees)) {
      u.assignees = [...u.assignees];
    }
    if (u.tags && Array.isArray(u.tags)) {
      u.tags = [...u.tags];
    }

    u = normalizeIncomingScheduleFields(stripEmptyIncomingViewState(u as Partial<ProjectNode>), (u.type || node.type) as ProjectNode['type']) as UpdateRecord;

    // Apply all other fields from the safe copy (with checklist removed above)
    // CRITICAL: Create a new node object to trigger React re-renders
    // Object.assign mutates in place, preventing React from detecting changes
    let updatedNode = { ...node, ...u, updatedAt: new Date().toISOString() } as ProjectNode;
    updatedNode = ensureNodeViewState(updatedNode);
    if (updatedNode.type !== 'task' && updatedNode.type !== 'group') {
      delete updatedNode.checklist;
      delete (updatedNode as ProjectNode & { checklists?: ChecklistItem[] }).checklists;
    } else if (Array.isArray(updatedNode.checklist)) {
      (updatedNode as ProjectNode & { checklists?: ChecklistItem[] }).checklists = [...updatedNode.checklist];
    }
    if ((updatedNode.type === 'task' && updates.type !== 'group' && updates.type !== 'milestone' && updates.type !== 'document') || updates.type === 'task') {
      updatedNode = { ...updatedNode, ...normalizeTaskStateUpdate(updatedNode as Partial<ProjectNode>, updatedNode as Partial<ProjectNode>) } as ProjectNode;
    }

    // Replace the node in the array with the new object
    const nodeIndex = resolvedNodeIndex >= 0 ? resolvedNodeIndex : cloned.nodes.findIndex(n => n.id === nodeId);
    if (nodeIndex >= 0) {
      cloned.nodes[nodeIndex] = updatedNode;
    }
    recomputeCanonicalRootNodeIds(cloned);
    touchProjectData(cloned);
  }
  return cloned;
}

export function deleteNode(data: ProjectData, nodeId: string): ProjectData {
  const idsToDelete = new Set([nodeId, ...getAllDescendantIds(data, nodeId)]);
  return deleteExactNodes(data, idsToDelete);
}

/** Apply the server's explicit single/batch deletion set without cascading. */
export function deleteExactNodes(data: ProjectData, idsToDelete: ReadonlySet<string>): ProjectData {
  const cloned = cloneProjectData(data);
  cloned.nodes = cloned.nodes.filter(n => !idsToDelete.has(n.id));
  cloned.nodes.forEach(node => {
    if (node.parentId && idsToDelete.has(node.parentId)) node.parentId = undefined;
    if (node.dependencies) node.dependencies = node.dependencies.filter(id => !idsToDelete.has(id));
  });
  recomputeCanonicalRootNodeIds(cloned);
  touchProjectData(cloned);
  return cloned;
}

export function moveNode(data: ProjectData, nodeId: string, targetParentId: string | null, targetIndex: number): ProjectData {
  const cloned = cloneProjectData(data);
  const node = cloned.nodes.find(n => n.id === nodeId);
  if (!node) return data;
  
  // Prevent circular references
  let parentId = targetParentId;
  const visitedParentIds = new Set<string>();
  while (parentId) {
    if (parentId === nodeId || visitedParentIds.has(parentId)) return data;
    visitedParentIds.add(parentId);
    const parent = cloned.nodes.find(n => n.id === parentId);
    parentId = parent?.parentId || null;
  }
  
  const oldParentId = node.parentId;
  node.parentId = targetParentId || undefined;
  
  // Reorder siblings - update both general order and Gantt-specific order
  const reorder = (parentId: string | null | undefined) => {
    const siblings = cloned.nodes.filter(n => (n.parentId || null) === (parentId || null)).sort((a, b) => getGanttOrder(a) - getGanttOrder(b));
    siblings.forEach((n, i) => {
      n.order = i;
      setGanttOrder(n, i); // Update Gantt-specific order
    });
  };
  
  const targetSiblings = cloned.nodes
    .filter(n => (n.parentId || null) === (targetParentId || null) && n.id !== nodeId)
    .sort((a, b) => getGanttOrder(a) - getGanttOrder(b));
  targetSiblings.splice(targetIndex, 0, node);
  targetSiblings.forEach((n, i) => {
    n.order = i;
    setGanttOrder(n, i); // Update Gantt-specific order
  });
  
  if (oldParentId !== targetParentId) reorder(oldParentId);
  
  // Keep root node order authoritative in optimistic state too.
  recomputeCanonicalRootNodeIds(cloned);
  touchProjectData(cloned);
  
  return cloned;
}

// Apply operations
// Operation handling now delegated to registry pattern
// Import the registry for clean operation dispatching
import { applyOperationViaRegistry } from './operations/handlers/operationRegistry';

/**
 * Apply an operation to project data
 * Uses operation registry for clean, maintainable handler dispatch
 */
export function applyOperation(data: ProjectData, op: NodeOperation): ProjectData {
  return applyOperationViaRegistry(data, op);
}

// Data integrity utilities
export function sanitizeProjectData(
  data: ProjectData,
  options?: { logRepairs?: boolean }
): ProjectData {
  const sanitized = cloneProjectData(data);
  let fixedCount = 0;
  const legacySettings = (sanitized as ProjectData & {
    settings?: {
      darkMode?: boolean;
      autoSync?: boolean;
      syncInterval?: number;
      isCompactView?: boolean;
      menuOrder?: string[];
      visibilityTierDefault?: 'internal' | 'partner' | 'client';
    };
  }).settings;

  if (!sanitized.metadata) {
    sanitized.metadata = { settings: { darkMode: legacySettings?.darkMode ?? false, autoSync: legacySettings?.autoSync ?? true, syncInterval: legacySettings?.syncInterval ?? 30, isCompactView: legacySettings?.isCompactView ?? false, menuOrder: legacySettings?.menuOrder ?? [], visibilityTierDefault: legacySettings?.visibilityTierDefault ?? 'internal' } };
    fixedCount++;
  } else if (!sanitized.metadata.settings) {
    sanitized.metadata.settings = { darkMode: legacySettings?.darkMode ?? false, autoSync: legacySettings?.autoSync ?? true, syncInterval: legacySettings?.syncInterval ?? 30, isCompactView: legacySettings?.isCompactView ?? false, menuOrder: legacySettings?.menuOrder ?? [], visibilityTierDefault: legacySettings?.visibilityTierDefault ?? 'internal' };
    fixedCount++;
  } else if (!sanitized.metadata.settings.visibilityTierDefault) {
    sanitized.metadata.settings.visibilityTierDefault = legacySettings?.visibilityTierDefault ?? 'internal';
    fixedCount++;
  }

  const dedupedNodes: ProjectNode[] = [];
  const seenNodeIds = new Set<string>();
  for (let index = sanitized.nodes.length - 1; index >= 0; index -= 1) {
    const node = sanitized.nodes[index];
    if (seenNodeIds.has(node.id)) {
      fixedCount++;
      continue;
    }
    seenNodeIds.add(node.id);
    dedupedNodes.push(node);
  }
  sanitized.nodes = dedupedNodes.reverse();

  const nodeIds = new Set(sanitized.nodes.map(n => n.id));
  
  // Initialize viewState for all nodes
  sanitized.nodes.forEach(node => {
    if (!node.viewState || Object.keys(node.viewState).length === 0) {
      initializeViewState(node);
      fixedCount++;
    }

    if (node.type === 'task') {
      const normalizedTaskState = normalizeTaskStateUpdate(node as Partial<ProjectNode>, node as Partial<ProjectNode>) as Partial<ProjectNode>;
      const hadTaskStateDrift =
        normalizedTaskState.status !== node.status ||
        normalizedTaskState.completionState !== node.completionState ||
        normalizedTaskState.progress !== node.progress ||
        JSON.stringify(normalizedTaskState.viewState?.kanban || null) !== JSON.stringify(node.viewState?.kanban || null);

      if (hadTaskStateDrift) {
        Object.assign(node, normalizedTaskState);
        fixedCount++;
      }
    }
  });
  
  // Fix self-parent references
  sanitized.nodes.forEach(node => {
    if (node.parentId && node.parentId === node.id) {
      log.warn('sanitizeProjectData', `Fixed self-parent reference for node: ${node.id}`);
      node.parentId = undefined;
      fixedCount++;
    }

    // Fix references to non-existent parents
    if (node.parentId && !nodeIds.has(node.parentId)) {
      log.warn('sanitizeProjectData', `Fixed orphaned parent reference for node: ${node.id} (parent: ${node.parentId})`);
      node.parentId = undefined;
      fixedCount++;
    }

    // Fix invalid dependencies
    if (node.dependencies && node.dependencies.length > 0) {
      const validDeps = node.dependencies.filter(depId => {
        if (depId === node.id) {
          log.warn('sanitizeProjectData', `Removed self-dependency for node: ${node.id}`);
          return false;
        }
        if (!nodeIds.has(depId)) {
          log.warn('sanitizeProjectData', `Removed invalid dependency for node: ${node.id} (dep: ${depId})`);
          return false;
        }
        return true;
      });
      if (validDeps.length !== node.dependencies.length) {
        node.dependencies = validDeps;
        fixedCount++;
      }
    }
  });

  // Fix indirect circular parent chains by promoting the offending node to root.
  const parentMap = new Map(sanitized.nodes.map(node => [node.id, node.parentId || null]));
  sanitized.nodes.forEach(node => {
    const visited = new Set<string>([node.id]);
    let cursor = node.parentId || null;
    while (cursor) {
      if (visited.has(cursor)) {
        log.warn('sanitizeProjectData', `Fixed circular parent chain for node: ${node.id}`);
        node.parentId = undefined;
        fixedCount++;
        break;
      }
      visited.add(cursor);
      cursor = parentMap.get(cursor) || null;
    }
  });

  const repairedInvalidContainerTypes = repairInvalidContainerNodeTypes(sanitized.nodes);
  if (repairedInvalidContainerTypes > 0) {
    fixedCount += repairedInvalidContainerTypes;
  }

  // Recompute canonical rootNodeIds so stale root ordering never survives partial updates.
  const canonicalRootNodeIds = buildCanonicalRootNodeIds(sanitized.nodes);
  if (JSON.stringify(sanitized.rootNodeIds) !== JSON.stringify(canonicalRootNodeIds)) {
    sanitized.rootNodeIds = canonicalRootNodeIds;
    fixedCount++;
  }

  if (fixedCount > 0 && options?.logRepairs !== false) {
    log.warn('sanitizeProjectData', `Fixed ${fixedCount} data integrity issues`);
  }
  
  return sanitized;
}

const createSeededNodeId = (): string =>
  `node-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const seedProjectNode = (partial: Partial<ProjectNode>): ProjectNode => {
  const children = Array.isArray(partial.children)
    ? partial.children.map((child) => seedProjectNode(child))
    : undefined;
  const id = typeof partial.id === 'string' && partial.id.trim().length > 0
    ? partial.id
    : createSeededNodeId();

  return {
    ...partial,
    id,
    type: partial.type || 'task',
    // i18n-raw-ui-literal-exempt: Persisted default task title; never localize stored user data.
    title: typeof partial.title === 'string' ? partial.title : 'New Item',
    children,
    order: typeof partial.order === 'number' ? partial.order : 0,
    completionState: partial.completionState ?? 'not-started',
    priority: partial.priority ?? 'medium',
    progress: typeof partial.progress === 'number' ? partial.progress : 0,
    assignees: Array.isArray(partial.assignees) ? partial.assignees : [],
    tags: Array.isArray(partial.tags) ? partial.tags : [],
    dependencies: Array.isArray(partial.dependencies) ? partial.dependencies : [],
    linkedDocs: Array.isArray(partial.linkedDocs) ? partial.linkedDocs : [],
    attachments: Array.isArray(partial.attachments) ? partial.attachments : [],
    attachmentCount: typeof partial.attachmentCount === 'number' ? partial.attachmentCount : 0,
    hasImages: partial.hasImages === true,
    hasDocuments: partial.hasDocuments === true,
    viewState: partial.viewState ?? {},
    version: typeof partial.version === 'number' ? partial.version : 1,
    createdAt: typeof partial.createdAt === 'string' && partial.createdAt ? partial.createdAt : new Date().toISOString(),
    updatedAt: typeof partial.updatedAt === 'string' && partial.updatedAt ? partial.updatedAt : new Date().toISOString(),
    customFields: partial.customFields ?? {},
  };
};

// Initialize
export function initializeProjectData(nodes: Partial<ProjectNode>[] = []): ProjectData {
  const seededNodes = nodes.map((node) => seedProjectNode(node));
  const flattenedNodes = flattenTree(seededNodes);
  return {
    version: '1.0.0',
    projectId: `project-${Date.now()}`,
    projectName: 'My Project',
    nodes: flattenedNodes,
    rootNodeIds: buildCanonicalRootNodeIds(flattenedNodes),
    kanbanColumns: DEFAULT_KANBAN_COLUMNS.map(column => ({ ...column })),
    files: [],
    fileAuditLogs: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    metadata: {
      settings: {
        darkMode: false,
        autoSync: true,
        syncInterval: 30,
        isCompactView: false,
        menuOrder: [],
        visibilityTierDefault: 'internal',
      }
    },
  };
}

// Helpers
const OBJECT_ID_LIKE_RE = /^[a-fA-F0-9]{24}$/;
const UUID_LIKE_RE = /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/;
const OPAQUE_ASSIGNEE_TOKEN_RE = /^(?:[a-fA-F0-9]{24}|[a-fA-F0-9-]{32,})$/;
const NULLISH_ASSIGNEE_RE = /^(null|undefined)$/i;
export const UNASSIGNED_ASSIGNEE_FILTER_VALUE = '__unassigned__';
export const AGENT_ASSIGNEE_PREFIX = 'agent:';
export const AGENT_DELEGATE_ASSIGNEE_ID = `${AGENT_ASSIGNEE_PREFIX}delegate`;
export const isAgentAssigneeId = (assignee?: string | null): boolean =>
  typeof assignee === 'string' && assignee.trim().startsWith(AGENT_ASSIGNEE_PREFIX);

export const normalizeAssigneeValue = (assignee?: string | null): string | null => {
  if (typeof assignee !== 'string') return null;
  const normalizedAssignee = assignee.trim();
  if (normalizedAssignee.length === 0) return null;
  if (normalizedAssignee === UNASSIGNED_ASSIGNEE_FILTER_VALUE) return null;
  if (NULLISH_ASSIGNEE_RE.test(normalizedAssignee)) return null;
  return normalizedAssignee;
};
export const getNodeAssignees = (node: ProjectNode): string[] => {
  const primaryAssignee = normalizeAssigneeValue(node.assignee);
  if (primaryAssignee) {
    return [primaryAssignee];
  }

  const firstAssignee = Array.isArray(node.assignees)
    ? node.assignees.map((assignee) => normalizeAssigneeValue(assignee)).find((assignee): assignee is string => assignee !== null)
    : null;

  return firstAssignee ? [firstAssignee] : [];
};
export const isNodeUnassigned = (node: ProjectNode): boolean => getNodeAssignees(node).length === 0;
const normalizeAssigneeDisplayLabel = (assignee: string, label?: string | null): string => {
  const normalizedAssignee = normalizeAssigneeValue(assignee);
  if (!normalizedAssignee) {
    return 'Unassigned';
  }
  if (isAgentAssigneeId(normalizedAssignee)) {
    return 'Agent';
  }
  const normalizedLabel = typeof label === 'string' ? label.trim() : '';
  if (normalizedLabel.length > 0 && !(OBJECT_ID_LIKE_RE.test(normalizedAssignee) && normalizedLabel === normalizedAssignee)) {
    return normalizedLabel;
  }
  if (OBJECT_ID_LIKE_RE.test(normalizedAssignee) || UUID_LIKE_RE.test(normalizedAssignee) || OPAQUE_ASSIGNEE_TOKEN_RE.test(normalizedAssignee)) {
    return 'Unknown user';
  }
  return normalizedAssignee;
};
export const getNodeAssigneeDisplayNames = (
  node: Pick<ProjectNode, 'assignee' | 'assignees' | 'assigneeDisplayNames'>,
  resolveAssigneeLabel?: (assignee: string) => string
): string[] => {
  const assignees = getNodeAssignees(node as ProjectNode);
  if (assignees.length === 0) return [];

  const explicitDisplayNames = Array.isArray(node.assigneeDisplayNames)
    ? node.assigneeDisplayNames.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    : [];

  return assignees.map((assignee, index) => normalizeAssigneeDisplayLabel(
    assignee,
    explicitDisplayNames[index] || resolveAssigneeLabel?.(assignee) || assignee
  ));
};
export const getNodeCompletionPercentage = (node: ProjectNode): number => {
  if (node.type === 'task' && node.checklist?.length) return Number((node.checklist.filter(i => i.checked).length / node.checklist.length * 100).toFixed(2));
  if (typeof node.progress === 'number') return Math.max(0, Math.min(100, node.progress));
  if (node.status === 'done' || node.completionState === 'completed') return 100;
  if (node.status === 'in-progress' || node.completionState === 'in-progress') return 50;
  return 0;
};

// Backward compatibility
export const buildNodeMap = <T extends ProjectNode>(nodes: T[]): Map<string, T> =>
  new Map(nodes.map(n => [n.id, { ...n } as T]));
export const normalizeSingleAssigneeList = (assignees: string[] = []): string[] => {
  const primaryAssignee = assignees
    .map((assignee) => normalizeAssigneeValue(assignee))
    .find((assignee): assignee is string => assignee !== null);
  return primaryAssignee ? [primaryAssignee] : [];
};
export const setNodeAssignees = (node: ProjectNode, assignees: string[]): ProjectNode => {
  const normalizedAssignees = normalizeSingleAssigneeList(assignees);
  return {
    ...node,
    assignees: normalizedAssignees,
    assignee: normalizedAssignees[0] || null,
  };
};
